import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useJobs } from '../../Hooks/useJobs';
import JobsHeader from './components/JobsHeader';
import JobsFilters from './components/JobsFilters';
import JobsTable from './components/JobsTable';
import JobsBulkActions from './components/JobsBulkActions';
import UpdateJobModal from './components/UpdateJobModal';
import { canAccess } from '../../utils/permissions';
import { WrenchScrewdriverIcon } from '@heroicons/react/24/outline';

export default function Jobs() {
    const queryClient = useQueryClient();
    const [selectedJobs, setSelectedJobs] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState('All');
    const [selectedJobType, setSelectedJobType] = useState('All');

    const [showStatusMenu, setShowStatusMenu] = useState(false);
    const [showJobTypeMenu, setShowJobTypeMenu] = useState(false);

    const { jobs, isLoading, deleteJob, updateJob, isDeleting, isUpdating } = useJobs();
    
    const [selectedJobForEdit, setSelectedJobForEdit] = useState(null);
    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);

    const canCreateJob = canAccess('create_job');
    const canDeleteJob = canAccess('delete_job');

    if (!canAccess("view_jobs")) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 text-center text-sm font-medium">
                <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-6">
                    <WrenchScrewdriverIcon className="w-10 h-10 text-red-600" />
                </div>
                <h2 className="text-3xl font-black text-gray-900 mb-2 tracking-tight">Access Restricted</h2>
                <p className="text-gray-500 max-w-md font-medium">
                    You don't have the required permissions to view jobs.
                </p>
            </div>
        );
    }

    const filteredJobs = jobs.filter(job => {
        if (selectedStatus !== 'All' && job.status !== selectedStatus) return false;
        if (selectedJobType !== 'All' && job.title !== selectedJobType) return false;
        return true;
    });

    const toggleJob = (id) => {
        setSelectedJobs(prev =>
            prev.includes(id) ? prev.filter(j => j !== id) : [...prev, id]
        );
    };

    const toggleSelectAll = () => {
        if (selectAll) {
            setSelectedJobs([]);
            setSelectAll(false);
        } else {
            setSelectedJobs(filteredJobs.map(j => j.id));
            setSelectAll(true);
        }
    };

    const handleUpdateSuccess = () => {
        queryClient.invalidateQueries(['companyJobs']);
    };

    const handleCloseModal = () => {
        setIsUpdateModalOpen(false);
        setSelectedJobForEdit(null);
    };

    return (
        <section className="min-h-screen text-sm">
            <JobsHeader jobs={filteredJobs} canCreateJob={canCreateJob} />

            <JobsBulkActions
                selectedCount={selectedJobs.length}
                onClear={() => { setSelectedJobs([]); setSelectAll(false); }}
                canDeleteJob={canDeleteJob}
            />

            <div className="px-4 py-5">
                <JobsFilters
                    selectedStatus={selectedStatus}
                    setSelectedStatus={setSelectedStatus}
                    selectedJobType={selectedJobType}
                    setSelectedJobType={setSelectedJobType}
                    showStatusMenu={showStatusMenu}
                    setShowStatusMenu={setShowStatusMenu}
                    showJobTypeMenu={showJobTypeMenu}
                    setShowJobTypeMenu={setShowJobTypeMenu}
                />

                <JobsTable
                    jobs={filteredJobs}
                    isLoading={isLoading}
                    selectedJobs={selectedJobs}
                    selectAll={selectAll}
                    toggleJob={toggleJob}
                    toggleSelectAll={toggleSelectAll}
                    onDelete={deleteJob}
                    onEdit={(job) => {
                        setSelectedJobForEdit(job);
                        setIsUpdateModalOpen(true);
                    }}
                    isDeleting={isDeleting}
                    canDeleteJob={canDeleteJob}
                />
            </div>

            <UpdateJobModal
                job={selectedJobForEdit}
                isOpen={isUpdateModalOpen}
                onClose={handleCloseModal}
                onSuccess={handleUpdateSuccess}
            />
        </section>
    );
}